import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useCreateDoctor } from "@/services/query/doctors.query"
import { useState } from "react"
import { toast } from "sonner"
import { Users2, GraduationCap, Phone, BadgeCheck, MapPin } from "lucide-react"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { LOCATIONS } from "@/lib/constants"

const initialState = {
  name: "",
  designation: "",
  qualifications: "",
  phone: null,
  location: "",
}

export function AddDoctorDialog({ open, onOpenChange }) {
  const [formData, setFormData] = useState(initialState)
  const createDoctorMutation = useCreateDoctor()

  const handleSubmit = async (e) => {
    e.preventDefault()
    const submitData = {
      ...formData,
      // Only include phone if it has a value
      phone: formData.phone?.trim() || null,
    }
    try {
      await createDoctorMutation.mutateAsync(submitData)
      setFormData(initialState)
      onOpenChange(false)
      toast.success("Doctor added successfully")
    } catch (error) {
      toast.error(error.message || "Failed to add doctor")
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users2 className="h-5 w-5 text-blue-500" />
            Add New Doctor
          </DialogTitle>
          <DialogDescription>
            Fill in the doctor's information. All fields are required except
            phone number.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-6 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name" className="flex items-center gap-2">
                <BadgeCheck className="h-4 w-4 text-blue-500" />
                Full Name
              </Label>
              <Input
                id="name"
                name="name"
                placeholder="Dr. John Doe"
                value={formData.name}
                onChange={handleChange}
                className="border-blue-100 focus:border-blue-200"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label
                  htmlFor="designation"
                  className="flex items-center gap-2"
                >
                  <Users2 className="h-4 w-4 text-blue-500" />
                  Designation
                </Label>
                <Input
                  id="designation"
                  name="designation"
                  placeholder="Senior Cardiologist"
                  value={formData.designation}
                  onChange={handleChange}
                  className="border-blue-100 focus:border-blue-200"
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="phone" className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-blue-500" />
                  Phone Number (Optional)
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  placeholder="+1 234 567 890"
                  value={formData.phone || ""}
                  onChange={handleChange}
                  className="border-blue-100 focus:border-blue-200"
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label
                htmlFor="qualifications"
                className="flex items-center gap-2"
              >
                <GraduationCap className="h-4 w-4 text-blue-500" />
                Qualifications
              </Label>
              <Textarea
                id="qualifications"
                name="qualifications"
                placeholder="MBBS, MD, DM, Fellowship in Cardiology"
                value={formData.qualifications}
                onChange={handleChange}
                className="border-blue-100 focus:border-blue-200 min-h-[80px]"
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="location" className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-blue-500" />
                Location
              </Label>
              <Select
                name="location"
                value={formData.location}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, location: value }))
                }
                required
              >
                <SelectTrigger className="border-blue-100 focus:border-blue-200">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {LOCATIONS.map((location) => (
                    <SelectItem key={location} value={location}>
                      {location}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createDoctorMutation.isPending}
              className="bg-blue-500 hover:bg-blue-600"
            >
              {createDoctorMutation.isPending ? "Adding..." : "Add Doctor"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
